/**
 * Daily Mission data definitions
 * Missions are seeded by date (UTC) so every player gets the same missions per day
 */

export type MissionType = 'tap' | 'buyProducer' | 'earnIp' | 'combo' | 'goldenEvent';
export type MissionDifficulty = 'easy' | 'normal' | 'hard';

export interface MissionTemplate {
  id: string;
  type: MissionType;
  nameKey: string;
  descKey: string;
  difficulty: MissionDifficulty;
  target: number;
  reward: MissionReward;
}

export interface MissionReward {
  docs: number;
  boostSeconds?: number;  // if present, grant a CPS boost for N seconds
  boostType?: 'cps2x' | 'cps3x' | 'cps5x';
}

export interface DailyMission {
  templateId: string;
  progress: number;
  target: number;
  claimed: boolean;
}

/**
 * Mission pool - templates to pick from daily
 */
export const MISSION_TEMPLATES: MissionTemplate[] = [
  // --- Easy (10) ---
  { id: 'tap_100', type: 'tap', nameKey: 'mission_tap_100', descKey: 'mission_tap_100_desc', difficulty: 'easy', target: 100, reward: { docs: 1 } },
  { id: 'tap_300', type: 'tap', nameKey: 'mission_tap_300', descKey: 'mission_tap_300_desc', difficulty: 'easy', target: 300, reward: { docs: 1 } },
  { id: 'tap_200', type: 'tap', nameKey: 'mission_tap_200', descKey: 'mission_tap_200_desc', difficulty: 'easy', target: 200, reward: { docs: 1 } },
  { id: 'buy_5', type: 'buyProducer', nameKey: 'mission_buy_5', descKey: 'mission_buy_5_desc', difficulty: 'easy', target: 5, reward: { docs: 1 } },
  { id: 'buy_3', type: 'buyProducer', nameKey: 'mission_buy_3', descKey: 'mission_buy_3_desc', difficulty: 'easy', target: 3, reward: { docs: 1 } },
  { id: 'combo_10', type: 'combo', nameKey: 'mission_combo_10', descKey: 'mission_combo_10_desc', difficulty: 'easy', target: 10, reward: { docs: 1 } },
  { id: 'combo_15', type: 'combo', nameKey: 'mission_combo_15', descKey: 'mission_combo_15_desc', difficulty: 'easy', target: 15, reward: { docs: 1 } },
  { id: 'golden_1', type: 'goldenEvent', nameKey: 'mission_golden_1', descKey: 'mission_golden_1_desc', difficulty: 'easy', target: 1, reward: { docs: 1 } },
  { id: 'earn_10k', type: 'earnIp', nameKey: 'mission_earn_10k', descKey: 'mission_earn_10k_desc', difficulty: 'easy', target: 10_000, reward: { docs: 1 } },
  { id: 'earn_100k', type: 'earnIp', nameKey: 'mission_earn_100k', descKey: 'mission_earn_100k_desc', difficulty: 'easy', target: 100_000, reward: { docs: 1 } },

  // --- Normal (10) ---
  { id: 'tap_1000', type: 'tap', nameKey: 'mission_tap_1000', descKey: 'mission_tap_1000_desc', difficulty: 'normal', target: 1000, reward: { docs: 2 } },
  { id: 'tap_500', type: 'tap', nameKey: 'mission_tap_500', descKey: 'mission_tap_500_desc', difficulty: 'normal', target: 500, reward: { docs: 2 } },
  { id: 'tap_700', type: 'tap', nameKey: 'mission_tap_700', descKey: 'mission_tap_700_desc', difficulty: 'normal', target: 700, reward: { docs: 2 } },
  { id: 'buy_20', type: 'buyProducer', nameKey: 'mission_buy_20', descKey: 'mission_buy_20_desc', difficulty: 'normal', target: 20, reward: { docs: 2 } },
  { id: 'buy_10', type: 'buyProducer', nameKey: 'mission_buy_10', descKey: 'mission_buy_10_desc', difficulty: 'normal', target: 10, reward: { docs: 2 } },
  { id: 'combo_50', type: 'combo', nameKey: 'mission_combo_50', descKey: 'mission_combo_50_desc', difficulty: 'normal', target: 50, reward: { docs: 2, boostSeconds: 30, boostType: 'cps2x' } },
  { id: 'combo_30', type: 'combo', nameKey: 'mission_combo_30', descKey: 'mission_combo_30_desc', difficulty: 'normal', target: 30, reward: { docs: 2 } },
  { id: 'golden_3', type: 'goldenEvent', nameKey: 'mission_golden_3', descKey: 'mission_golden_3_desc', difficulty: 'normal', target: 3, reward: { docs: 2 } },
  { id: 'golden_2', type: 'goldenEvent', nameKey: 'mission_golden_2', descKey: 'mission_golden_2_desc', difficulty: 'normal', target: 2, reward: { docs: 2 } },
  { id: 'earn_1m', type: 'earnIp', nameKey: 'mission_earn_1m', descKey: 'mission_earn_1m_desc', difficulty: 'normal', target: 1_000_000, reward: { docs: 2, boostSeconds: 30, boostType: 'cps2x' } },

  // --- Hard (10) ---
  { id: 'tap_5000', type: 'tap', nameKey: 'mission_tap_5000', descKey: 'mission_tap_5000_desc', difficulty: 'hard', target: 5000, reward: { docs: 3, boostSeconds: 300, boostType: 'cps2x' } },
  { id: 'tap_3000', type: 'tap', nameKey: 'mission_tap_3000', descKey: 'mission_tap_3000_desc', difficulty: 'hard', target: 3000, reward: { docs: 3 } },
  { id: 'buy_50', type: 'buyProducer', nameKey: 'mission_buy_50', descKey: 'mission_buy_50_desc', difficulty: 'hard', target: 50, reward: { docs: 3 } },
  { id: 'buy_30', type: 'buyProducer', nameKey: 'mission_buy_30', descKey: 'mission_buy_30_desc', difficulty: 'hard', target: 30, reward: { docs: 3 } },
  { id: 'combo_100', type: 'combo', nameKey: 'mission_combo_100', descKey: 'mission_combo_100_desc', difficulty: 'hard', target: 100, reward: { docs: 3, boostSeconds: 300, boostType: 'cps3x' } },
  { id: 'combo_75', type: 'combo', nameKey: 'mission_combo_75', descKey: 'mission_combo_75_desc', difficulty: 'hard', target: 75, reward: { docs: 3 } },
  { id: 'golden_5', type: 'goldenEvent', nameKey: 'mission_golden_5', descKey: 'mission_golden_5_desc', difficulty: 'hard', target: 5, reward: { docs: 3 } },
  { id: 'golden_4', type: 'goldenEvent', nameKey: 'mission_golden_4', descKey: 'mission_golden_4_desc', difficulty: 'hard', target: 4, reward: { docs: 3 } },
  { id: 'earn_1b', type: 'earnIp', nameKey: 'mission_earn_1b', descKey: 'mission_earn_1b_desc', difficulty: 'hard', target: 1_000_000_000, reward: { docs: 3, boostSeconds: 300, boostType: 'cps3x' } },
  { id: 'earn_10m', type: 'earnIp', nameKey: 'mission_earn_10m', descKey: 'mission_earn_10m_desc', difficulty: 'hard', target: 10_000_000, reward: { docs: 3, boostSeconds: 60, boostType: 'cps2x' } },
];

/** All-complete bonus: 5 classified docs */
export const ALL_COMPLETE_BONUS_DOCS = 5;

/** Number of missions selected daily */
export const DAILY_MISSION_COUNT = 4;

/**
 * Get today's date string in UTC (YYYY-MM-DD)
 */
export function getUtcDateString(now = Date.now()): string {
  const d = new Date(now);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Seeded random number generator (simple mulberry32)
 * Deterministic based on date seed so all players get same missions
 */
function seedFromDate(dateStr: string): number {
  let h = 0;
  for (let i = 0; i < dateStr.length; i++) {
    h = Math.imul(31, h) + dateStr.charCodeAt(i) | 0;
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let s = seed;
  return () => {
    s |= 0; s = s + 0x6D2B79F5 | 0;
    let t = Math.imul(s ^ s >>> 15, 1 | s);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/**
 * Generate daily missions for a given date
 * Picks 1 easy, 2 normal, 1 hard = 4 missions
 */
export function generateDailyMissions(dateStr: string): DailyMission[] {
  const rng = mulberry32(seedFromDate(dateStr));

  const easy = MISSION_TEMPLATES.filter(m => m.difficulty === 'easy');
  const normal = MISSION_TEMPLATES.filter(m => m.difficulty === 'normal');
  const hard = MISSION_TEMPLATES.filter(m => m.difficulty === 'hard');

  // Pick without duplicate difficulty slots
  const pickUnique = (arr: MissionTemplate[], count: number): MissionTemplate[] => {
    const shuffled = [...arr].sort(() => rng() - 0.5);
    return shuffled.slice(0, count);
  };

  const selected: MissionTemplate[] = [
    ...pickUnique(easy, 1),
    ...pickUnique(normal, 2),
    ...pickUnique(hard, 1),
  ];

  return selected.map(t => ({
    templateId: t.id,
    progress: 0,
    target: t.target,
    claimed: false,
  }));
}

/**
 * Get the template for a mission by ID
 */
export function getMissionTemplate(templateId: string): MissionTemplate | undefined {
  return MISSION_TEMPLATES.find(t => t.id === templateId);
}
